import {
  Avatar,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Container,
  Fade,
  Grid,
  Grow,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { MODE_DISPLAY_NAME } from '../../../common/constants';
import { StyledPaperItem } from '../../../common/mui-components';
import { Player, DetailedPlayer, PreventAnswer, Mode } from '../types';
import { GameClock } from './GameClock';
import { GameHeaderMobile } from './GameHeaderMobile';
import { Players } from './Players';
import { PoolPlayLeaderboard } from './PoolPlayLeaderboard';
import './SubmittedByStamp.scss';
import './styles.css';
import { CustomQuestionTestingData } from '../../custom-questions';
import { Penalty, Team } from '@par/shared/types/game';
import { GiBroadsword, GiDevilMask, GiRaiseZombie } from 'react-icons/gi';
import { ChallengeLifes } from './ChallengeLifes';
import { StarterKitConfig } from '../../starter-kits/types';
import { Socket } from 'socket.io-client';

let timeout: any;

type PropTypes = {
  bannedCategories?: string[];
  category: string;
  categorySelections?: string[];
  categorySet: string;
  challengeTowerAltTimeout?: number;
  customQuestionTestingData?: CustomQuestionTestingData;
  daily?: boolean;
  dailyChallengeTower?: number;
  dailyTheme?: string;
  delayBeforeQuestionRevealCount: number;
  demo?: boolean;
  demons?: boolean;
  displayGameClock: boolean;
  fastNext?: boolean;
  freezeGameClock: boolean;
  gameClock: number;
  gameType: 'casual' | 'pro';
  ghostChallenge?: boolean;
  ghostChallengeFrom?: string;
  ghostChallengeTo?: string;
  goToNextQuestionEarly?: boolean;
  league?: boolean;
  lifes?: number;
  matchMaking?: boolean;
  matchMakingSeededBy?: string;
  mode: Mode;
  myTeam?: Team;
  nextQuestionDelayCount: number;
  otherTeam?: Team;
  par: number;
  penalty?: Penalty;
  players?: Record<string, Player>;
  playerSelf: Player;
  playerThem?: Player;
  questionIndex: number;
  ranked?: boolean;
  roundOver?: boolean;
  spectate: boolean;
  socket: Socket;
  starterKit?: StarterKitConfig;
  submittedByUsername?: string;
  timerLimitReached: boolean;
  totalNumQuestions: number | undefined;
  tournament?: number;
  showHeart?: boolean;
  showUsedHeart?: boolean;
  survivalMode?: 'endurance' | 'challenge' | 'demon';
  variant?: 'normal' | 'survival' | 'starter-kit' | 'turbo';
};

export const GameHeader = ({
  bannedCategories,
  category,
  categorySelections,
  categorySet,
  challengeTowerAltTimeout,
  customQuestionTestingData,
  daily,
  dailyChallengeTower,
  dailyTheme,
  delayBeforeQuestionRevealCount,
  demo,
  demons,
  displayGameClock,
  fastNext,
  freezeGameClock,
  gameClock,
  gameType,
  ghostChallenge,
  ghostChallengeFrom,
  ghostChallengeTo,
  goToNextQuestionEarly,
  league,
  lifes,
  matchMaking,
  matchMakingSeededBy,
  mode,
  myTeam,
  nextQuestionDelayCount,
  otherTeam,
  par,
  penalty,
  players,
  playerSelf,
  playerThem,
  questionIndex,
  ranked,
  roundOver,
  spectate,
  socket,
  starterKit,
  submittedByUsername,
  timerLimitReached,
  totalNumQuestions,
  tournament,
  showHeart,
  showUsedHeart,
  survivalMode,
  variant,
}: PropTypes) => {
  const [timerBeforeQuestionReveal, setTimerBeforeQuestionReveal] = useState(0);
  const [displayCategoryAndPar, setDisplayCategoryAndPar] = useState(true);
  const [connectCounter, setConnectCounter] = useState(0);
  const [disconnectCounter, setDisconnectCounter] = useState(0);

  const handleConnect = () => {
    setConnectCounter((prev) => prev + 1);
  };

  const handleDisconnect = () => {
    setDisconnectCounter((prev) => prev + 1);
  };

  useEffect(() => {
    if (!socket) return;
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, []);

  useEffect(() => {
    setDisplayCategoryAndPar(true);
  }, [connectCounter]);

  useEffect(() => {
    clearTimeout(timeout);
  }, [disconnectCounter]);

  useEffect(() => {
    if (nextQuestionDelayCount === 2) setTimerBeforeQuestionReveal(25);
    if (nextQuestionDelayCount === 1) setTimerBeforeQuestionReveal(50);
    if (nextQuestionDelayCount === 0) setTimerBeforeQuestionReveal(75);
  }, [nextQuestionDelayCount]);

  useEffect(() => {
    if (delayBeforeQuestionRevealCount === 1) setTimerBeforeQuestionReveal(100);
    if (delayBeforeQuestionRevealCount === 0) setTimerBeforeQuestionReveal(0);
  }, [delayBeforeQuestionRevealCount]);

  useEffect(() => {
    if (gameClock === 0) return;
    if (timerLimitReached || allPlayersCorrectAnswer()) {
      clearTimeout(timeout);
      const displayCategoryAndParDelay = restrictedFastNext() ? 0 : 2000;
      timeout = setTimeout(() => {
        setDisplayCategoryAndPar(false);
      }, displayCategoryAndParDelay);
      return;
    }
    setDisplayCategoryAndPar(true);
  }, [timerLimitReached, playerSelf]);

  useEffect(() => {
    clearTimeout(timeout);
    setDisplayCategoryAndPar(true);
  }, [questionIndex]);

  const allPlayersCorrectAnswer = () => {
    if (mode !== 'sit-n-go' && mode !== 'teams') {
      return playerThem
        ? playerSelf.correctAnswer && playerThem.correctAnswer
        : playerSelf.correctAnswer;
    }
    return false;
  };

  const theme = useTheme();
  const smAndDownBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  // ensure the fastNext option is only applied for specific game modes
  const restrictedFastNext = () => {
    if (!fastNext) {
      return false;
    }
    if (matchMaking) {
      return true;
    }
    if (ghostChallenge && ghostChallengeFrom === playerSelf?.profileId) {
      return true;
    }
    if (mode === 'solo') {
      if (!daily) {
        return true;
      }
    }
    return false;
  };

  const scoreLabelValue = (score: string | number, penalty?: number) => (
    <Stack spacing={0} alignItems="center" justifyContent="center" marginX={1}>
      <Typography
        variant="overline"
        mb={-1}
        zIndex={1}
        sx={{ fontSize: { sm: 12, md: 16 } }}
      >
        Score
      </Typography>
      <Typography
        variant="h6"
        zIndex={1}
        sx={{
          textShadow: `#fff 0px 0px 1px, #fff 10px 0px 20px, #fff 0px 0px 16px`,
        }}
      >
        {score}
      </Typography>

      <Typography variant="overline" zIndex={1} mt={2}>
        Penalty
      </Typography>
      {penalty ? (
        <Avatar
          sx={{
            width: 20,
            height: 20,
            bgcolor: theme.palette.warning.main,
            zIndex: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {penalty || 0}
          </Typography>
        </Avatar>
      ) : (
        <Typography variant="subtitle1">--</Typography>
      )}
    </Stack>
  );

  const generateGameModeLabel = () => {
    let label = '';
    if (demo) {
      return 'Practice';
    }
    if (tournament) {
      return 'Tournament';
    }
    if (league) {
      return 'League';
    }
    if (demons) {
      return 'Face Your Demons';
    }
    if (starterKit) {
      return starterKit.starterKitName;
    }
    if (mode !== 'solo' && ranked) {
      label = label + 'Ranked - ';
    }
    if (mode !== 'solo' && !ranked) {
      label = label + 'Friendly - ';
    }
    label = label + MODE_DISPLAY_NAME[mode] + ' - ';
    if (variant !== 'survival') {
      label = label + gameType + ' - ';
    }
    if (variant !== 'survival') {
      if (categorySet === 'none') {
        if (categorySelections?.length === 1) {
          label = label + categorySelections.join();
        } else {
          label = label + 'MIX';
        }
      } else {
        label = label + categorySet;
      }
    }
    if (variant === 'survival') {
      if (survivalMode === 'challenge') {
        label = label + 'Survival - Challenge Tower';
      }
      if (survivalMode === 'endurance') {
        label = label + 'Survival - Endurance';
      }
    }
    return label;
  };

  const desktopHeader = (
    <Stack direction="row" justifyContent="space-evenly" alignItems="center">
      <StyledPaperItem
        sx={{
          padding: 2,
          overflow: 'hidden',
          visibility:
            !spectate || (spectate && mode !== 'sit-n-go')
              ? 'visible'
              : 'hidden',
        }}
      >
        {playerSelf || myTeam ? (
          <Grid container width={mode === 'teams' ? 350 : 250} spacing={1}>
            <Grid item xs={9}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {mode === 'teams' && myTeam ? (
                    <Players
                      correctAnswer={myTeam.correctAnswer}
                      currentScore={myTeam.currentScore}
                      mode={mode}
                      parScore={myTeam.parScore}
                      players={players}
                      teamMembers={myTeam.members}
                      timerLimitReached={timerLimitReached}
                    />
                  ) : (
                    <Players
                      disconnected={playerSelf.disconnected}
                      avatarPublicId={playerSelf.avatarPublicId}
                      avatarUrl={playerSelf.avatarUrl}
                      currentScore={playerSelf.currentScore}
                      parScore={playerSelf.parScore}
                      correctAnswer={playerSelf.correctAnswer}
                      timerLimitReached={timerLimitReached}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  {mode === 'teams' && myTeam ? (
                    <Stack>
                      {Object.values(players)
                        .filter((p) => myTeam.members.includes(p.profileId))
                        .map((p) => (
                          <Typography
                            textAlign="center"
                            width="100%"
                            color={
                              myTeam.correctAnswer &&
                              myTeam.correctAnswerPlayerUsername === p.username
                                ? 'success.main'
                                : undefined
                            }
                            sx={{
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                            }}
                          >
                            {p.username}
                          </Typography>
                        ))}
                    </Stack>
                  ) : (
                    <>
                      <Typography
                        textAlign="center"
                        variant="body1"
                        width="100%"
                      >
                        {playerSelf.username}
                      </Typography>
                      <Typography
                        visibility={!playerSelf.location ? 'hidden' : 'visible'}
                        textAlign="center"
                        variant="subtitle2"
                        width="100%"
                        sx={{
                          color: 'primary.main',
                        }}
                      >
                        {playerSelf?.location}
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={3} alignSelf="center">
              {mode === 'teams' && myTeam
                ? scoreLabelValue(
                    myTeam.parScore !== undefined
                      ? myTeam.parScore > 0
                        ? `+${myTeam.parScore}`
                        : myTeam.parScore
                      : 'N/A',
                    myTeam.totalPenalty
                  )
                : scoreLabelValue(
                    playerSelf.parScore !== undefined
                      ? playerSelf.parScore > 0
                        ? `+${playerSelf.parScore}`
                        : playerSelf.parScore
                      : 'N/A',
                    playerSelf.totalPenalty
                  )}
            </Grid>
          </Grid>
        ) : (
          <Grid container width={200} spacing={1}></Grid> // need this placeholder to keep the clock centered when spectating
        )}
      </StyledPaperItem>
      <Stack
        direction="column"
        justifyContent="center"
        minWidth={'300px'}
        position="relative"
      >
        {submittedByUsername && (gameClock >= 20 || goToNextQuestionEarly) ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              top: '15%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <Grow in={displayCategoryAndPar} timeout={500}>
              <Box
                className="submitted-by-stamp submitted-by-stamp-username"
                textAlign={'center'}
              >
                Question by:
                <br />
                {submittedByUsername}
              </Box>
            </Grow>
          </Box>
        ) : null}
        {customQuestionTestingData ? (
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.warning.main,
              textAlign: 'center',
            }}
          >
            Preview Mode
          </Typography>
        ) : daily ? (
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.dailies.main,
              textAlign: 'center',
            }}
          >
            Daily: {dailyTheme}
          </Typography>
        ) : dailyChallengeTower ? (
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.survival.main,
              textAlign: 'center',
            }}
          >
            Daily Tower: Day {dailyChallengeTower}
          </Typography>
        ) : (
          <Typography
            variant="overline"
            sx={{
              color: league
                ? theme.palette.ranked.main
                : theme.palette.tertiary.main,
              textAlign: 'center',
              width: '100%',
            }}
          >
            {generateGameModeLabel()}
          </Typography>
        )}
        {bannedCategories ? (
          <Stack
            direction={'row'}
            spacing={2}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 1,
            }}
          >
            {bannedCategories.map((c) => (
              <Typography
                variant="caption"
                className="angled-strikethrough straight"
                sx={{
                  color: '#b0917f',
                }}
              >
                {c}
              </Typography>
            ))}
          </Stack>
        ) : null}
        {variant === 'survival' &&
        (survivalMode === 'endurance' || survivalMode === 'challenge') ? (
          <Stack
            spacing={2}
            direction={'row'}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <GiBroadsword
              style={{
                fontSize: '24px',
                color: '#C4E4D4',
              }}
            />{' '}
            <Typography variant="h6" fontWeight={600} letterSpacing={2}>
              SLAY Q{questionIndex + 1}
            </Typography>
            <GiRaiseZombie
              style={{
                fontSize: '24px',
                color: theme.palette.survival.main,
              }}
            />{' '}
          </Stack>
        ) : variant === 'survival' && survivalMode === 'demon' ? (
          <Stack
            spacing={2}
            direction={'row'}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <GiDevilMask
              style={{
                fontSize: '24px',
                color: theme.palette.demon.main,
              }}
            />{' '}
            <Typography variant="h6" fontWeight={600} letterSpacing={2}>
              VANQUISH Q{questionIndex + 1}
            </Typography>
            <GiDevilMask
              style={{
                fontSize: '24px',
                color: theme.palette.demon.main,
              }}
            />{' '}
          </Stack>
        ) : (
          <Typography variant="caption" textAlign="center" width="100%">
            {questionIndex + 1} of {totalNumQuestions}
          </Typography>
        )}
        <Box
          sx={{
            height: 90,
            display: !displayGameClock ? 'none' : 'flex',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Fade in={displayGameClock}>
            {/* A bug in the Grow mui transitions requires this <div>
          https://github.com/mui/material-ui/issues/27154#issuecomment-1117386458 */}
            <div>
              <GameClock
                freezeGameClock={freezeGameClock}
                par={par}
                gameClock={gameClock}
                spectate={spectate}
              />

              {survivalMode === 'challenge' &&
              (challengeTowerAltTimeout === 18 ||
                challengeTowerAltTimeout === 19) ? (
                <Typography
                  fontWeight={600}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    marginTop: '-5px',
                    marginRight: '-40px',
                    backgroundColor: theme.palette.error.dark,
                    borderRadius: '10px',
                    paddingX: 1,
                  }}
                >
                  stops at {challengeTowerAltTimeout}
                </Typography>
              ) : null}
            </div>
          </Fade>
        </Box>
        <Box
          sx={{
            height: 90,
            display: displayGameClock ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Fade in={!displayGameClock}>
            <CircularProgress
              variant="determinate"
              value={timerBeforeQuestionReveal}
            />
          </Fade>
        </Box>
        <Grow in={displayCategoryAndPar} timeout={1500}>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            marginTop={1}
          >
            <Chip
              label={category || 'None'}
              color="info"
              variant="outlined"
              size={smAndDownBreakpoint ? 'small' : 'medium'}
              sx={{
                width: 'fit-content',
                fontSize: { xs: 'small', md: 'medium' },
                alignSelf: 'center',
                display: { xs: 'none', sm: 'flex' },
              }}
            />
            <Chip
              label={
                <Typography>
                  PAR <strong>{par}</strong>
                </Typography>
              }
              color="info"
              variant="outlined"
              size={smAndDownBreakpoint ? 'small' : 'medium'}
              sx={{
                boxShadow: `${theme.palette.primary.main} 0px 0px 5px -1px, ${theme.palette.primary.main} 0px 0px 1px 0px, ${theme.palette.primary.main} 0px 0px 0px 0px`,
                width: 'fit-content',
                fontSize: { xs: 'small', md: 'medium' },
                alignSelf: 'center',
                display: { xs: 'none', sm: 'flex' },
              }}
            />
          </Stack>
        </Grow>
      </Stack>
      {variant === 'survival' && survivalMode === 'demon' ? (
        <Box width={'200px'}>
          <ChallengeLifes
            lifes={lifes}
            maxLives={3}
            questionIndex={questionIndex}
            showHeart={showHeart}
            showUsedHeart={showUsedHeart}
          />
        </Box>
      ) : (
        <StyledPaperItem
          sx={{
            overflow: 'hidden',
            padding: 2,
            visibility:
              mode === 'teams' ||
              (mode === 'match' &&
                ghostChallengeFrom !== playerSelf?.profileId &&
                matchMakingSeededBy !== playerSelf?.profileId)
                ? 'visible'
                : 'hidden',
          }}
        >
          <Grid container width={mode === 'teams' ? 350 : 250} spacing={1}>
            <Grid item xs={3} alignSelf="center">
              {mode === 'teams' && otherTeam
                ? scoreLabelValue(
                    otherTeam?.parScore !== undefined
                      ? otherTeam?.parScore > 0
                        ? `+${otherTeam?.parScore}`
                        : otherTeam?.parScore
                      : 'N/A',
                    otherTeam.totalPenalty
                  )
                : scoreLabelValue(
                    playerThem?.parScore !== undefined
                      ? playerThem?.parScore > 0
                        ? `+${playerThem?.parScore}`
                        : playerThem?.parScore
                      : 'N/A',
                    playerThem?.totalPenalty
                  )}
            </Grid>
            <Grid item xs={9}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {mode === 'teams' && otherTeam ? (
                    <Players
                      correctAnswer={otherTeam?.correctAnswer}
                      currentScore={otherTeam?.currentScore}
                      mode={mode}
                      parScore={otherTeam?.parScore}
                      players={players}
                      teamMembers={otherTeam?.members}
                      timerLimitReached={timerLimitReached}
                    />
                  ) : (
                    <Players
                      avatarPublicId={playerThem?.avatarPublicId || ''}
                      avatarUrl={playerThem?.avatarUrl || ''}
                      correctAnswer={playerThem?.correctAnswer}
                      currentScore={playerThem?.currentScore}
                      disconnected={playerThem?.disconnected}
                      ghostChallenge={ghostChallenge}
                      matchMaking={matchMaking}
                      parScore={playerThem?.parScore}
                      penalty={
                        playerThem ? penalty?.[playerThem.profileId] : undefined
                      }
                      timerLimitReached={timerLimitReached}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  {mode === 'teams' && otherTeam ? (
                    <Stack>
                      {Object.values(players)
                        .filter((p) => otherTeam.members.includes(p.profileId))
                        .map((p) => (
                          <Typography
                            textAlign="center"
                            width="100%"
                            color={
                              otherTeam.correctAnswer &&
                              otherTeam.correctAnswerPlayerUsername ===
                                p.username
                                ? 'success.main'
                                : undefined
                            }
                            sx={{
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                            }}
                          >
                            {p.username}
                          </Typography>
                        ))}
                    </Stack>
                  ) : (
                    <>
                      <Typography
                        textAlign="center"
                        variant="body1"
                        width="100%"
                      >
                        {playerThem?.username}
                      </Typography>
                      <Typography
                        visibility={
                          !playerThem?.location ? 'hidden' : 'visible'
                        }
                        textAlign="center"
                        variant="subtitle2"
                        width="100%"
                        sx={{
                          color: 'primary.main',
                        }}
                      >
                        {playerThem?.location}
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </StyledPaperItem>
      )}
    </Stack>
  );

  // const mobileHeader = (
  //   <GameHeaderMobile
  //     category={category}
  //     delayBeforeQuestionRevealCount={delayBeforeQuestionRevealCount}
  //     displayGameClock={displayGameClock}
  //     freezeGameClock={freezeGameClock}
  //     gameClock={gameClock}
  //     mode={mode}
  //     nextQuestionDelayCount={nextQuestionDelayCount}
  //     par={par}
  //     penalty={penalty}
  //     playerSelf={playerSelf}
  //     playerThem={playerThem}
  //     questionIndex={questionIndex}
  //     timerLimitReached={timerLimitReached}
  //     totalNumQuestions={totalNumQuestions}
  //     spectate={spectate}
  //   ></GameHeaderMobile>
  // );

  return (
    <>
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{desktopHeader}</Box>
      {/* <Box sx={{ display: { xs: 'block', sm: 'none' } }}>{mobileHeader}</Box> */}
    </>
  );
};
